// src/modules/Chat/ChatPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar,
    Badge,
    Button,
    Empty,
    Input,
    Layout,
    List,
    Modal,
    Space,
    Tag,
    Tooltip,
    Upload,
    message as antdMessage,
    Typography,
    DatePicker,
    Select, // <-- ADDED
} from "antd";
import {
    PlusOutlined,
    PaperClipOutlined,
    SendOutlined,
    TeamOutlined,
    UserOutlined,
    UserAddOutlined as UserPlus,
    SearchOutlined as SearchIcon,
    CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
    fetchMyRooms,
    fetchMessages,
    createRoom,
    sendTextMessage,
    sendFilesMessage,
    addParticipants, // kept (not used now) to honor "don't remove anything"
} from "../../../api/chatApi";
import { createMeeting } from "../../../api/meetingApi"; // <-- keep this

import { withBase } from "../../../config/env";

const { Text } = Typography;
const { Sider, Content } = Layout;

/** Derive display for room header */
const getRoomDisplay = (room, me) => {
    if (!room) return { title: "Chat", subtitle: "" };
    if (Number(room.is_group) === 1) {
        return { title: room.name || "Group", subtitle: room.participants || "" };
    }
    const names = (room.participants || "").split(",").map(s => s.trim()).filter(Boolean);
    const other =
        names.length <= 1 ? room.name || "Direct Chat" : names.find(n => n !== (me?.name || "")) || names[0];
    return { title: other, subtitle: "Direct chat" };
};

const bubbleStyle = (mine) => ({
    maxWidth: "72%",
    background: mine ? "#1677ff" : "#ffffff",
    color: mine ? "#fff" : "#222",
    padding: 12,
    borderRadius: 14,
    boxShadow: mine ? "0 2px 8px rgba(22,119,255,.25)" : "0 2px 8px rgba(0,0,0,.06)",
    border: mine ? "1px solid rgba(22,119,255,.4)" : "1px solid #f0f0f0",
});

/** Render a special card if the message content encodes a meeting */
const MeetingCard = ({ mine, content }) => {
    // Expecting content like: MEETING::{"subject":"...","datetime":"ISO","link":"https://...","mentions":[{id,name}]}
    let data = null;
    try {
        const raw = String(content || "");
        if (raw.startsWith("MEETING::")) {
            const jsonStr = raw.slice("MEETING::".length);
            data = JSON.parse(jsonStr);
        }
    } catch {
        // fall through
    }
    if (!data) return null;

    const when = data.datetime ? dayjs(data.datetime) : null;
    const pretty = when?.isValid() ? when.format("MMM D, YYYY • hh:mm A") : "—";
    const link = String(data.link || "").trim();
    const subject = String(data.subject || "Meeting").trim();

    // Mentions (array of {id,name} or names)
    const mentionsRaw = Array.isArray(data.mentions) ? data.mentions : [];
    const mentionNames = mentionsRaw
        .map((m) => (typeof m === "string" ? m : m?.name))
        .filter(Boolean);

    return (
        <div
            style={{
                border: "1px dashed #1677ff",
                borderRadius: 12,
                padding: 12,
                background: mine ? "rgba(255,255,255,0.18)" : "#f8fbff",
                color: mine ? "#fff" : "#1f1f1f",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <CalendarOutlined />
                <span style={{ fontWeight: 700 }}>Meeting</span>
            </div>
            <div style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.75 }}>Subject:</span>{" "}
                <span style={{ fontWeight: 600 }}>{subject}</span>
            </div>
            <div style={{ marginBottom: 10 }}>
                <span style={{ opacity: 0.75 }}>When:</span>{" "}
                <span>{pretty}</span>
            </div>
            {mentionNames.length ? (
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ opacity: 0.75 }}>With:</span>
                    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                        {mentionNames.map((n, i) => (
                            <Tag key={i} color={mine ? "geekblue" : "blue"} style={{ margin: 0 }}>
                                @{n}
                            </Tag>
                        ))}
                    </span>
                </div>
            ) : null}
            {link ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                        display: "inline-block",
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #1677ff",
                        color: mine ? "#fff" : "#1677ff",
                        background: mine ? "transparent" : "#fff",
                        textDecoration: "none",
                        fontWeight: 600,
                    }}
                >
                    Join Meeting
                </a>
            ) : null}
        </div>
    );
};

const MessageBubble = ({ meId, msg, onMediaLoad }) => {
    const mine = Number(msg.sender_id) === Number(meId);
    const isMeeting =
      typeof msg.content === "string" && msg.content.startsWith("MEETING::");
  
    // Prefer backend-provided alias; fallbacks for other shapes
    const displayName =
      mine
        ? ""
        : (msg.sender_name ||
           msg.senderName ||
           msg.user_name ||
           msg.name ||
           "Member");
  
    return (
      <div
        style={{
          display: "flex",
          justifyContent: mine ? "flex-end" : "flex-start",
          margin: "8px 0",
          padding: "0 8px",
        }}
      >
        <div style={bubbleStyle(mine)}>
          {/* Sender name */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 900,
              marginBottom: 6,
              color: mine ? "#ffffff" : "#0d0f12",
              opacity: 0.9,
            }}
          >
            {displayName}
          </div>
  
          {/* Content */}
          {isMeeting ? (
            <MeetingCard mine={mine} content={msg.content} />
          ) : msg.type === "text" ? (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {msg.content}
            </div>
          ) : (
            <div>
              {msg.content ? (
                <div style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>
                  {msg.content}
                </div>
              ) : null}
              {(msg.attachments || []).map((a) => {
                const isImage = (a.mime_type || "").startsWith("image/");
                const isPDF = (a.mime_type || "").toLowerCase().includes("pdf");
                return (
                  <div key={a.id} style={{ marginBottom: 8 }}>
                    {isImage ? (
                      <a href={withBase(a.file_path)} target="_blank" rel="noreferrer">
                        <img
                          alt={a.file_name}
                          src={withBase(a.file_path)}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 360,
                            borderRadius: 10,
                            display: "block",
                            objectFit: "contain",
                          }}
                          onLoad={onMediaLoad}
                        />
                      </a>
                    ) : (
                      <a
                        href={withBase(a.file_path)}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 12px",
                          background: "#f5f5f5",
                          borderRadius: 6,
                          color: "#333",
                        }}
                      >
                        {isPDF ? "📄" : "📎"}
                        <Text ellipsis style={{ maxWidth: 200 }}>
                          {a.file_name}
                        </Text>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
  
          {/* Timestamp */}
          <div
            style={{
              fontSize: 11,
              opacity: 0.65,
              marginTop: 6,
              textAlign: mine ? "right" : "left",
              lineHeight: 1.2,
            }}
          >
            {dayjs(msg.created_at).format("MMM D, HH:mm")}
          </div>
        </div>
      </div>
    );
  };
// De-dupe by id
const dedupeById = (arr = []) => {
    const seen = new Set();
    return (arr || []).filter((m) => {
        const key = m?.id != null ? String(m.id) : null;
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export default function ChatPage() {
    const me = useMemo(() => JSON.parse(localStorage.getItem("user")) || { id: 1, name: "Me" }, []);

    // Sidebar
    const [rooms, setRooms] = useState([]);

    const [roomsLoading, setRoomsLoading] = useState(false);
    const [roomsFilter, setRoomsFilter] = useState("");

    // Active chat
    const [activeRoom, setActiveRoom] = useState(null);

    // Messages
    const [messages, setMessages] = useState([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);

    // Refs & scrolling
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const lastMsgIdRef = useRef(null);
    const messagesRef = useRef([]);
    const pollingInitRef = useRef({});

    useEffect(() => {
        messagesRef.current = messages;
        const maxId = messages.reduce((mx, m) => (m && typeof m.id === "number" && m.id > mx ? m.id : mx), -Infinity);
        lastMsgIdRef.current = maxId === -Infinity ? null : maxId;
    }, [messages]);

    // Scroll to bottom function
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    // Auto-scroll when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages.length, scrollToBottom]);

    // Immediate scroll when changing rooms
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [activeRoom?.id]);

    // Create room modal
    const [openCreate, setOpenCreate] = useState(false);
    const [participantIds, setParticipantIds] = useState("");
    const [isGroup, setIsGroup] = useState(false);
    const [groupName, setGroupName] = useState("");

    // (Repurposed) Add members modal -> now "Members" modal
    const [openAddMembers, setOpenAddMembers] = useState(false);
    const [newMemberIds, setNewMemberIds] = useState(""); // kept to honor "don't remove anything"

    // Composer
    const [input, setInput] = useState("");
    const [files, setFiles] = useState([]);

    // --- Meeting modal state (ADDED) ---
    const [openMeeting, setOpenMeeting] = useState(false);
    const [meetingSubject, setMeetingSubject] = useState("");
    const [meetingDateTime, setMeetingDateTime] = useState(dayjs().add(1, "hour"));
    const [meetingLink, setMeetingLink] = useState("");
    const [sendingMeeting, setSendingMeeting] = useState(false);
    const [mentionIds, setMentionIds] = useState([]); // NEW: selected participant IDs to mention

    // Sorted messages (oldest -> newest)
    const sortedMessages = useMemo(() => {
        const toNum = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : NaN;
        };
        const toTs = (v) => {
            const t = dayjs(v).valueOf();
            return Number.isFinite(t) ? t : 0;
        };

        const clean = dedupeById(messages);
        return clean.slice().sort((a, b) => {
            const ai = toNum(a?.id);
            const bi = toNum(b?.id);
            if (!Number.isNaN(ai) && !Number.isNaN(bi) && ai !== bi) {
                return ai - bi; // numeric id ascending
            }
            // fallback: created_at ascending
            return toTs(a?.created_at) - toTs(b?.created_at);
        });
    }, [messages]);

    // Load rooms
    const loadRooms = useCallback(async () => {
        setRoomsLoading(true);
        try {
            const data = await fetchMyRooms();
            const arr = data?.data || data || [];
            setRooms(arr);
            if (!activeRoom && arr[0]) setActiveRoom(arr[0]);
        } catch (e) {
            console.error(e);
            antdMessage.error("Failed to load chats");
        } finally {
            setRoomsLoading(false);
        }
    }, [activeRoom]);

    // Load messages (spinner)
    const loadMessages = useCallback(async (roomId) => {
        if (!roomId) return;
        setLoadingMsgs(true);
        try {
            const data = await fetchMessages(roomId, { limit: 200 });
            const arr = dedupeById(data?.data || data || []);
            setMessages(arr);
        } catch (e) {
            console.error(e);
            antdMessage.error("Failed to load messages");
        } finally {
            setLoadingMsgs(false);
        }
    }, []);

    // Polling (5s) — StrictMode safe
    useEffect(() => {
        const roomId = activeRoom?.id;
        if (!roomId) return;
        if (pollingInitRef.current[roomId]) return;
        pollingInitRef.current[roomId] = true;

        let cancelled = false;

        const tick = async () => {
            try {
                const data = await fetchMessages(roomId, { limit: 200 });
                const arr = dedupeById(data?.data || data || []);
                const incomingMaxId = arr.reduce(
                    (mx, m) => (m && typeof m.id === "number" && m.id > mx ? m.id : mx),
                    -Infinity
                );
                const incomingLastId = incomingMaxId === -Infinity ? null : incomingMaxId;

                if (!cancelled && (incomingLastId !== lastMsgIdRef.current || arr.length !== messagesRef.current.length)) {
                    setMessages(arr);
                }
            } catch {
                // silent
            }
        };

        // first load & then poll
        loadMessages(roomId);
        const interval = setInterval(tick, 5000);

        return () => {
            cancelled = true;
            clearInterval(interval);
            delete pollingInitRef.current[roomId];
        };
    }, [activeRoom?.id, loadMessages]);

    // Send text
    const onSend = async () => {
        const text = input.trim();
        if (!text || !activeRoom?.id) return;
        setInput("");
        try {
            await sendTextMessage(activeRoom.id, text);
            const data = await fetchMessages(activeRoom.id, { limit: 200 });
            setMessages(dedupeById(data?.data || data || []));
        } catch {
            antdMessage.error("Failed to send message");
        }
    };

    // Send files
    const onUpload = async () => {
        if (!activeRoom?.id || files.length === 0) return;
        try {
            await sendFilesMessage(
                activeRoom.id,
                files.map((f) => f.originFileObj)
            );
            setFiles([]);
            const data = await fetchMessages(activeRoom.id, { limit: 200 });
            setMessages(dedupeById(data?.data || data || []));
        } catch {
            antdMessage.error("Upload failed");
        }
    };

    // Create room
    const createNewRoom = async () => {
        const ids = participantIds
            .split(",")
            .map((s) => parseInt(s.trim(), 10))
            .filter(Boolean);

        if (!ids.length) {
            antdMessage.warning("Add at least one participant ID");
            return;
        }

        try {
            const payload = { is_group: isGroup ? 1 : 0, name: groupName || null, participant_ids: ids, created_by: me.id };
            const r = await createRoom(payload);
            const roomId = r?.data?.room_id ?? r?.room_id;

            setOpenCreate(false);
            setParticipantIds("");
            setGroupName("");
            setIsGroup(false);

            await loadRooms();
            if (roomId) {
                setActiveRoom(prev => {
                    const found = (rooms || []).find(x => Number(x.id) === Number(roomId));
                    return found || { id: roomId, is_group: isGroup ? 1 : 0, name: groupName || null };
                });
            }
            antdMessage.success("Chat created");
        } catch {
            antdMessage.error("Failed to create chat");
        }
    };

    // ===== Members logic (replaces Add Members UI) =====
    const members = useMemo(() => {
        // Parse IDs and Names from activeRoom
        const ids = String(activeRoom?.participants_id || "")
            .split(",")
            .map((s) => parseInt(String(s).trim(), 10))
            .filter((n) => Number.isInteger(n) && n > 0);

        const names = String(activeRoom?.participants || "")
            .split(",")
            .map((s) => String(s).trim())
            .filter(Boolean);

        // zip by index; ignore mismatch tails safely
        const len = Math.min(ids.length, names.length);
        const zipped = [];
        for (let i = 0; i < len; i += 1) {
            zipped.push({ id: ids[i], name: names[i] });
        }

        // Deduplicate by id
        const seen = new Set();
        return zipped.filter((m) => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
    }, [activeRoom?.participants_id, activeRoom?.participants]);

    const startDirectChatWith = async (memberId, memberName) => {
        if (!memberId || Number(memberId) === Number(me?.id)) {
            antdMessage.info("You cannot start a direct chat with yourself.");
            return;
        }
        try {
            const payload = {
                is_group: 0,
                name: null,
                participant_ids: [Number(memberId)],
                created_by: Number(me?.id) || undefined,
            };
            const r = await createRoom(payload);
            const roomId = r?.data?.room_id ?? r?.room_id;

            antdMessage.success(`Direct chat with "${memberName}" ${r?.data?.reused ? "opened" : "created"}.`);

            // Refresh rooms and focus that chat
            await loadRooms();
            if (roomId) {
                setActiveRoom((prev) => {
                    const found = (rooms || []).find((x) => Number(x.id) === Number(roomId));
                    return found || { id: roomId, is_group: 0, name: memberName };
                });
            }
        } catch (e) {
            antdMessage.error(e?.response?.data?.message || "Failed to start direct chat");
        }
    };

    const onMemberClick = (m) => {
        Modal.confirm({
            title: `Start a direct chat with ${m.name}?`,
            okText: "Yes",
            cancelText: "No",
            onOk: () => startDirectChatWith(m.id, m.name),
        });
    };
    // ===================================================

    // === Meeting scheduling (ADDED) ===
    const openScheduleModal = () => {
        if (!activeRoom?.id) {
            antdMessage.warning("Select a chat first.");
            return;
        }
        setMeetingSubject("");
        setMeetingDateTime(dayjs().add(1, "hour"));
        setMeetingLink("");
        setMentionIds([]); // reset mentions
        setOpenMeeting(true);
    };

    const onSendMeeting = async () => {
        if (!activeRoom?.id) return;

        const subject = meetingSubject.trim();
        const dt = meetingDateTime;
        const link = meetingLink.trim();

        if (!subject) {
            antdMessage.warning("Please enter a subject.");
            return;
        }
        if (!dt || !dayjs(dt).isValid()) {
            antdMessage.warning("Please pick a valid date & time.");
            return;
        }
        if (!link) {
            antdMessage.warning("Please provide a meeting link.");
            return;
        }

        // Resolve selected member objects by ID (from the current group)
        const selectedMembers =
            (Array.isArray(mentionIds) ? mentionIds : [])
                .map((id) => members.find((m) => Number(m.id) === Number(id)))
                .filter(Boolean);

        // payload for the chat message (keeps your special card UI)
        const content = `MEETING::${JSON.stringify({
            subject,
            datetime: dayjs(dt).toISOString(),
            link,
            mentions: selectedMembers.map((m) => ({ id: m.id, name: m.name })), // show @mentions in card
        })}`;

        // Prepare values for the meetings table
        const meeting_date_time = dayjs(dt).format("YYYY-MM-DD HH:mm:ss"); // SQL-friendly
        const user_ids_csv = String(activeRoom?.participants_id || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .join(",");

        try {
            setSendingMeeting(true);

            // 1) Send to chat
            await sendTextMessage(activeRoom.id, content);
            const data = await fetchMessages(activeRoom.id, { limit: 200 });
            setMessages(dedupeById(data?.data || data || []));
            setOpenMeeting(false);
            antdMessage.success("Meeting shared to the chat");

            // 2) Persist in meetings table (non-blocking UX; errors handled separately)
            try {
                await createMeeting({
                    subject,
                    meeting_date_time, // "YYYY-MM-DD HH:mm:ss"
                    link,
                    user_ids: user_ids_csv || String(me?.id || ""), // fallback to self if missing
                });
            } catch (err) {
                console.error(err);
                antdMessage.warning("Chat sent, but saving meeting failed.");
            }
        } catch (e) {
            antdMessage.error(e?.response?.data?.message || "Failed to send meeting");
        } finally {
            setSendingMeeting(false);
        }
    };

    // ================================

    // First load rooms
    useEffect(() => {
        loadRooms();
    }, [loadRooms]);

    // Filter rooms
    const filteredRooms = useMemo(() => {
        const q = roomsFilter.trim().toLowerCase();
        if (!q) return rooms;
        return rooms.filter((r) => {
            const title = (r.name || "").toLowerCase();
            const participants = (r.participants || "").toLowerCase();
            return title.includes(q) || participants.includes(q);
        });
    }, [rooms, roomsFilter]);

    const { title: headerTitle, subtitle: headerSub } = getRoomDisplay(activeRoom, me);

    return (
        <Layout style={{
            height: "100vh",
            background: "#f7f9fb",
            overflow: "hidden"
        }}>
            {/* Sidebar */}
            <Sider
                width={340}
                className="chat-sider"
                style={{
                    background: "#fff",
                    borderRight: "1px solid #eef0f3",
                    boxShadow: "2px 0 12px rgba(0,0,0,0.04)",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* wrapper to fill 100% height inside sider-children */}
                <div className="chat-sider-body" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                    <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <div style={{ fontWeight: 700, fontSize: 18, color: "#1d1d1d" }}>Messages</div>
                        </div>
                        <Input
                            allowClear
                            prefix={<SearchIcon style={{ color: "#999" }} />}
                            placeholder="Search chats or people"
                            value={roomsFilter}
                            onChange={(e) => setRoomsFilter(e.target.value)}
                            style={{ borderRadius: 20, backgroundColor: "#f5f5f5", border: "none" }}
                        />
                    </div>

                    {/* this is the ONLY scrollable area */}
                    <div className="chat-sider-scroll" style={{ flex: "1 1 0", minHeight: 0, overflowY: "auto", padding: "8px 8px 0" }}>
                        <List
                            loading={roomsLoading}
                            itemLayout="horizontal"
                            dataSource={filteredRooms}
                            renderItem={(item) => {
                                const direct = Number(item.is_group) !== 1;
                                const names = (item.participants || "").split(",").map(s => s.trim()).filter(Boolean);
                                const otherName = direct && names.length > 1 ? names.find(n => n !== (me?.name || "")) || names[0] : null;

                                return (
                                    <List.Item
                                        onClick={() => setActiveRoom(item)}
                                        style={{
                                            cursor: "pointer",
                                            background: activeRoom?.id === item.id ? "#f0f6ff" : "#fff",
                                            border: activeRoom?.id === item.id ? "1px solid #cde2ff" : "1px solid #f0f0f0",
                                            borderRadius: 12,
                                            margin: "6px 0",
                                            padding: "10px 12px",
                                            transition: "all .15s",
                                        }}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Badge dot={false}>
                                                    <Avatar
                                                        icon={Number(item.is_group) === 1 ? <TeamOutlined /> : <UserOutlined />}
                                                        style={{ background: Number(item.is_group) === 1 ? "#e6f4ff" : "#f6ffed", color: "#1677ff" }}
                                                    />
                                                </Badge>
                                            }
                                            title={
                                                <div style={{ display: "flex", gap: 8, alignItems: "center", lineHeight: 1.2, minWidth: 0 }}>
                                                    <Text strong ellipsis={{ tooltip: true }} style={{ fontSize: 14, color: "#1d1d1d", maxWidth: 200, display: "inline-block" }}>
                                                        {Number(item.is_group) === 1 ? item.name || "Group" : otherName || item.name || "Direct Chat"}
                                                    </Text>
                                                    {Number(item.is_group) === 1 ? (
                                                        <Tag color="blue" style={{ fontSize: 11, padding: "0 6px", lineHeight: "18px", flexShrink: 0 }}>Group</Tag>
                                                    ) : null}
                                                </div>
                                            }
                                            description={
                                                <Text ellipsis style={{ fontSize: 12, color: "#666", lineHeight: 1.2, display: "block", maxWidth: 240 }}>
                                                    {Number(item.is_group) === 1 ? item.participants : "Direct chat"}
                                                </Text>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                </div>
            </Sider>

            {/* Main area */}
            <Content style={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                position: "relative"
            }}>
                {/* Header - fixed height */}
                <div
                    style={{
                        height: 64,
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        background: "#fff",
                        padding: "0 16px",
                        borderBottom: "1px solid #eef0f3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    {activeRoom ? (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%"
                        }}>
                            <Space size={12} align="center">
                                <Avatar
                                    size={40}
                                    icon={Number(activeRoom?.is_group) === 1 ? <TeamOutlined /> : <UserOutlined />}
                                    style={{
                                        background: Number(activeRoom?.is_group) === 1 ? "#e6f4ff" : "#f6ffed",
                                        color: "#1677ff",
                                    }}
                                />
                                <div>
                                    <div style={{
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: "#1d1d1d"
                                    }}>{headerTitle}</div>
                                    <div style={{
                                        fontSize: 12,
                                        color: "#666"
                                    }}>{headerSub}</div>
                                </div>
                            </Space>

                            <Space>
                                {/* Schedule button (ADDED) */}
                                <Button
                                    icon={<CalendarOutlined />}
                                    onClick={openScheduleModal}
                                    size="small"
                                >
                                    Schedule
                                </Button>

                                {Number(activeRoom?.is_group) === 1 ? (
                                    <Button
                                        icon={<TeamOutlined />}
                                        onClick={() => setOpenAddMembers(true)}
                                        size="small"
                                    >
                                        Members
                                    </Button>
                                ) : null}
                            </Space>
                        </div>
                    ) : (
                        <div style={{
                            fontWeight: 600,
                            color: "#1d1d1d"
                        }}>Select a chat</div>
                    )}
                </div>

                {/* Messages container - fixed height with scroll */}
                <div
                    ref={messagesContainerRef}
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "16px 8px",
                        background: "#f7f9fb",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {activeRoom ? (
                        loadingMsgs ? (
                            <div style={{
                                padding: 24,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                color: "#666"
                            }}>Loading messages...</div>
                        ) : sortedMessages.length ? (
                            <>
                                {sortedMessages.map((m) => (
                                    <MessageBubble key={m.id} meId={me.id} msg={m} onMediaLoad={scrollToBottom} />
                                ))}
                                <div ref={messagesEndRef} style={{ height: 8 }} />
                            </>
                        ) : (
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                flexDirection: "column",
                                gap: 8
                            }}>
                                <Empty
                                    description={
                                        <Text type="secondary" style={{ fontSize: 14 }}>
                                            No messages yet
                                        </Text>
                                    }
                                    imageStyle={{ height: 80 }}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Send a message to start the conversation
                                </Text>
                            </div>
                        )
                    ) : (
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            flexDirection: "column",
                            gap: 8
                        }}>
                            <Empty
                                description={
                                    <Text type="secondary" style={{ fontSize: 14 }}>
                                        Select a chat to start
                                    </Text>
                                }
                                imageStyle={{ height: 80 }}
                            />
                            {/* <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setOpenCreate(true)}
                                size="small"
                            >
                                Create new chat
                            </Button> */}
                        </div>
                    )}
                </div>

                {/* Composer - fixed height */}
                <div
                    style={{
                        padding: "12px 16px",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 5,
                        background: "#fff",
                        borderTop: "1px solid #eef0f3",
                    }}
                >
                    {activeRoom ? (
                        <div style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-end"
                        }}>
                            <Tooltip title="Attach files">
                                <Upload
                                    multiple
                                    fileList={files}
                                    beforeUpload={() => false}
                                    onChange={({ fileList }) => setFiles(fileList)}
                                    maxCount={10}
                                >
                                    <Button
                                        icon={<PaperClipOutlined />}
                                        style={{
                                            height: 40,
                                            width: 40,
                                            borderRadius: "50%"
                                        }}
                                    />
                                </Upload>
                            </Tooltip>
                            <Input.TextArea
                                placeholder="Type a message..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onPressEnter={(e) => {
                                    if (!e.shiftKey) {
                                        e.preventDefault();
                                        onSend();
                                    }
                                }}
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                style={{
                                    flex: 1,
                                    borderRadius: 20,
                                    padding: "8px 16px",
                                    resize: "none",
                                    borderColor: "#d9d9d9"
                                }}
                            />
                            {files.length > 0 ? (
                                <Button
                                    type="primary"
                                    onClick={onUpload}
                                    style={{
                                        height: 40,
                                        borderRadius: 20,
                                        padding: "0 16px"
                                    }}
                                >
                                    Send {files.length} file{files.length > 1 ? 's' : ''}
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={onSend}
                                    style={{
                                        height: 40,
                                        width: 40,
                                        borderRadius: "50%"
                                    }}
                                    disabled={!input.trim()}
                                />
                            )}
                        </div>
                    ) : null}
                </div>
            </Content>

            {/* New chat modal */}
            <Modal
                title="New Chat"
                open={openCreate}
                onOk={createNewRoom}
                onCancel={() => setOpenCreate(false)}
                okText="Create"
                okButtonProps={{ size: "middle" }}
                cancelButtonProps={{ size: "middle" }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="secondary">Enter user IDs separated by commas</Text>
                    <Input
                        value={participantIds}
                        onChange={(e) => setParticipantIds(e.target.value)}
                        placeholder="e.g. 12, 34"
                        style={{ marginBottom: 12 }}
                    />
                    <Space wrap>
                        <Button
                            type={isGroup ? "primary" : "default"}
                            onClick={() => setIsGroup((s) => !s)}
                            size="middle"
                        >
                            {isGroup ? "Group" : "Direct"}
                        </Button>
                        {isGroup && (
                            <Input
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Group name (optional)"
                                style={{ width: 200 }}
                            />
                        )}
                    </Space>
                </Space>
            </Modal>

            {/* Members modal (replaces Add Members functionality, but keeps structure) */}
            <Modal
                title={`Members${activeRoom?.name ? ` of ${activeRoom.name}` : ""}`}
                open={openAddMembers}
                onOk={() => setOpenAddMembers(false)}
                onCancel={() => setOpenAddMembers(false)}
                okText="Close"
                okButtonProps={{ size: "middle" }}
                cancelButtonProps={{ size: "middle" }}
                footer={[
                    <Button key="close" type="primary" onClick={() => setOpenAddMembers(false)}>
                        Close
                    </Button>
                ]}
            >
                {Number(activeRoom?.is_group) !== 1 ? (
                    <Text type="secondary">This is a direct chat. No group members to show.</Text>
                ) : members.length ? (
                    <List
                        dataSource={members.filter(m => Number(m.id) !== Number(me?.id))} // hide self from actions
                        renderItem={(m) => (
                            <List.Item
                                actions={[
                                    <Button key="dm" size="small" onClick={() => onMemberClick(m)}>
                                        Message
                                    </Button>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar
                                            icon={<UserOutlined />}
                                            style={{ background: "#f6ffed", color: "#52c41a" }}
                                        />
                                    }
                                    title={<span>{m.name}</span>}
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <Text type="secondary">No members found.</Text>
                )}
            </Modal>

            {/* Schedule Meeting modal (ADDED) */}
            <Modal
                title="Share Meeting"
                open={openMeeting}
                onOk={onSendMeeting}
                confirmLoading={sendingMeeting}
                onCancel={() => setOpenMeeting(false)}
                okText="Send"
                okButtonProps={{ size: "middle", disabled: sendingMeeting }}
                cancelButtonProps={{ size: "middle", disabled: sendingMeeting }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <div>
                        <Text strong>Subject</Text>
                        <Input
                            value={meetingSubject}
                            onChange={(e) => setMeetingSubject(e.target.value)}
                            placeholder="e.g. Project kickoff"
                        />
                    </div>

                    <div>
                        <Text strong>Date & Time</Text>
                        <DatePicker
                            showTime
                            value={meetingDateTime}
                            onChange={(val) => setMeetingDateTime(val)}
                            style={{ width: "100%" }}
                        />
                        <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                            {meetingDateTime && dayjs(meetingDateTime).isValid()
                                ? `Will send as: ${dayjs(meetingDateTime).format("MMM D, YYYY • hh:mm A")}`
                                : null}
                        </div>
                    </div>



                    <div>
                        <Text strong>Meeting Link</Text>
                        <Input
                            value={meetingLink}
                            onChange={(e) => setMeetingLink(e.target.value)}
                            placeholder="https://..."
                        />
                    </div>
                    {Number(activeRoom?.is_group) === 1 && members.length > 0 ? (
                        <div>
                            <Text strong>Mention Participants (optional)</Text>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select members to mention"
                                value={mentionIds}
                                onChange={setMentionIds}
                                style={{ width: "100%" }}
                                maxTagCount="responsive"
                                options={members
                                    .filter((m) => Number(m.id) !== Number(me?.id)) // usually you don't mention yourself
                                    .map((m) => ({ label: m.name, value: Number(m.id) }))}
                            />
                            <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                                Selected names will appear as @mentions in the meeting card.
                            </div>
                        </div>
                    ) : null}
                </Space>
            </Modal>
        </Layout>
    );
}
